import { createPopper } from '@popperjs/core';
import DOMPurify from 'dompurify';

const TOOLTIP_SHOW_CLASS_DELAY_MS = 1;
const TOOLTIP_REMOVE_DELAY_MS = 300;
const DEFAULT_TOUCH_AUTO_DISMISS_MS = 2000;
const TOOLTIP_CLEANUP_KEY = '__cTooltipCleanup__';
const TOOLTIP_CONTENT_UPDATE_KEY = '__cTooltipContentUpdate__';

let tooltipCounter = 0;

const nextTooltipId = () => {
    tooltipCounter += 1;
    return `tooltip-${tooltipCounter}`;
};

const isTouchOrCoarsePointerDevice = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const hasCoarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse), (any-pointer: coarse)').matches;
    const hasTouchPoints = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

    return hasCoarsePointer || hasTouchPoints;
};

const toTooltipContentString = (content) => {
    if (typeof content === 'string') {
        return content;
    }

    if (content === null || content === undefined) {
        return '';
    }

    return String(content);
};

const setTooltipContent = (innerElement, content, allowHtml) => {
    const nextContent = toTooltipContentString(content);

    if (allowHtml) {
        innerElement.innerHTML = DOMPurify.sanitize(nextContent);
        return;
    }

    innerElement.textContent = nextContent;
};

const createTooltipElement = (id, content, allowHtml) => {
    const tooltip = document.createElement('div');
    tooltip.id = id;
    tooltip.classList.add('tooltip', 'bs-tooltip-auto', 'fade');
    tooltip.setAttribute('role', 'tooltip');

    const arrow = document.createElement('div');
    arrow.classList.add('tooltip-arrow');
    arrow.setAttribute('data-popper-arrow', '');

    const inner = document.createElement('div');
    inner.classList.add('tooltip-inner');
    setTooltipContent(inner, content, allowHtml);

    tooltip.appendChild(arrow);
    tooltip.appendChild(inner);

    return tooltip;
};

const getTooltipContent = (value) => {
    return typeof value === 'string' ? value : (value?.content ?? '');
};

const getAllowHtml = (value) => {
    return typeof value === 'object' && value !== null && value.allowHtml === true;
};

const addTooltipElement = (el, tooltip, popperOptions, tooltipId, onOpen, onPopperCreate, onShowClassTimeoutCreate) => {
    el.setAttribute('aria-describedby', tooltipId);
    document.body.appendChild(tooltip);
    const popperInstance = createPopper(el, tooltip, popperOptions);
    onPopperCreate?.(popperInstance);

    const showClassTimeoutId = window.setTimeout(() => {
        tooltip.classList.add('show');
    }, TOOLTIP_SHOW_CLASS_DELAY_MS);
    onShowClassTimeoutCreate?.(showClassTimeoutId);

    onOpen?.();
};

const removeTooltipElement = (el, tooltip, onClose, onBeforeRemove) => {
    onBeforeRemove?.();
    el.removeAttribute('aria-describedby');
    tooltip.classList.remove('show');

    setTimeout(() => {
        tooltip.remove();
    }, TOOLTIP_REMOVE_DELAY_MS);

    onClose?.();
};

const toggleTooltipElement = (el, tooltip, popperOptions, tooltipId, onOpen, onClose, onPopperCreate, onBeforeRemove, onShowClassTimeoutCreate) => {
    const isVisible = tooltip.classList.contains('show');

    if (isVisible) {
        removeTooltipElement(el, tooltip, onClose, onBeforeRemove);
        return;
    }

    addTooltipElement(el, tooltip, popperOptions, tooltipId, onOpen, onPopperCreate, onShowClassTimeoutCreate);
};

const hasTrigger = (trigger, type) => {
    if (Array.isArray(trigger)) {
        return trigger.includes(type);
    }

    if (typeof trigger === 'string') {
        return trigger.includes(type);
    }

    return false;
};

export default {
    name: 'c-tooltip',
    mounted(el, binding) {
        const value = binding.value ?? '';
        const content = getTooltipContent(value);
        const allowHtml = getAllowHtml(value);
        const trigger = typeof value === 'string' ? 'hover' : (value.trigger ?? 'hover');
        const touchAutoDismissMs =
            typeof value === 'object' && value !== null && typeof value.touchAutoDismissMs === 'number'
                ? value.touchAutoDismissMs
                : DEFAULT_TOUCH_AUTO_DISMISS_MS;

        const offset = typeof value === 'object' && value !== null ? (value.offset ?? [0, 6]) : [0, 6];
        const placement = typeof value === 'object' && value !== null ? (value.placement ?? 'top') : 'top';

        const popperOptions = {
            placement,
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset,
                    },
                },
            ],
        };

        const tooltipId = nextTooltipId();
        const tooltip = createTooltipElement(tooltipId, content, allowHtml);
        const cleanupFns = [];

        const updateTooltipContent = (nextContent, nextAllowHtml = allowHtml) => {
            const innerElement = tooltip.querySelector('.tooltip-inner');
            if (innerElement) {
                setTooltipContent(innerElement, nextContent, nextAllowHtml);
            }
        };

        let popperInstance = null;
        let showClassTimeoutId = null;
        let touchDismissTimeoutId = null;
        let removeNextTapListener = null;

        const clearShowClassTimeout = () => {
            if (showClassTimeoutId !== null) {
                window.clearTimeout(showClassTimeoutId);
                showClassTimeoutId = null;
            }
        };

        const setShowClassTimeoutId = (timeoutId) => {
            clearShowClassTimeout();
            showClassTimeoutId = timeoutId;
        };

        const destroyPopperInstance = () => {
            popperInstance?.destroy();
            popperInstance = null;
        };

        const setPopperInstance = (instance) => {
            destroyPopperInstance();
            popperInstance = instance;
        };

        const teardownTooltipRuntime = () => {
            clearShowClassTimeout();
            destroyPopperInstance();
        };

        const clearTouchAutoDismiss = () => {
            if (touchDismissTimeoutId !== null) {
                window.clearTimeout(touchDismissTimeoutId);
                touchDismissTimeoutId = null;
            }

            if (removeNextTapListener) {
                removeNextTapListener();
                removeNextTapListener = null;
            }
        };

        const closeTooltip = () => {
            clearTouchAutoDismiss();
            removeTooltipElement(el, tooltip, clearTouchAutoDismiss, teardownTooltipRuntime);
        };

        const scheduleTouchAutoDismiss = () => {
            if (!isTouchOrCoarsePointerDevice()) {
                return;
            }

            clearTouchAutoDismiss();

            const onNextTap = (event) => {
                if (event.target instanceof Node && el.contains(event.target)) {
                    return;
                }

                clearTouchAutoDismiss();
                closeTooltip();
            };

            document.addEventListener('pointerdown', onNextTap, { capture: true });
            removeNextTapListener = () => {
                document.removeEventListener('pointerdown', onNextTap, true);
            };

            touchDismissTimeoutId = window.setTimeout(() => {
                closeTooltip();
            }, touchAutoDismissMs);
        };

        if (hasTrigger(trigger, 'click')) {
            const onClick = () => {
                toggleTooltipElement(
                    el,
                    tooltip,
                    popperOptions,
                    tooltipId,
                    scheduleTouchAutoDismiss,
                    clearTouchAutoDismiss,
                    setPopperInstance,
                    teardownTooltipRuntime,
                    setShowClassTimeoutId,
                );
            };

            el.addEventListener('click', onClick);
            cleanupFns.push(() => el.removeEventListener('click', onClick));
        }

        if (hasTrigger(trigger, 'focus')) {
            const onFocus = () => {
                addTooltipElement(el, tooltip, popperOptions, tooltipId, scheduleTouchAutoDismiss, setPopperInstance, setShowClassTimeoutId);
            };
            const onBlur = () => {
                removeTooltipElement(el, tooltip, clearTouchAutoDismiss, teardownTooltipRuntime);
            };

            el.addEventListener('focus', onFocus);
            el.addEventListener('blur', onBlur);

            cleanupFns.push(() => el.removeEventListener('focus', onFocus));
            cleanupFns.push(() => el.removeEventListener('blur', onBlur));
        }

        if (hasTrigger(trigger, 'hover')) {
            const onMouseEnter = () => {
                addTooltipElement(el, tooltip, popperOptions, tooltipId, scheduleTouchAutoDismiss, setPopperInstance, setShowClassTimeoutId);
            };
            const onMouseLeave = () => {
                removeTooltipElement(el, tooltip, clearTouchAutoDismiss, teardownTooltipRuntime);
            };

            el.addEventListener('mouseenter', onMouseEnter);
            el.addEventListener('mouseleave', onMouseLeave);

            cleanupFns.push(() => el.removeEventListener('mouseenter', onMouseEnter));
            cleanupFns.push(() => el.removeEventListener('mouseleave', onMouseLeave));
        }

        el[TOOLTIP_CLEANUP_KEY] = () => {
            clearTouchAutoDismiss();
            teardownTooltipRuntime();
            cleanupFns.forEach((cleanup) => cleanup());
            tooltip.remove();
        };
        el[TOOLTIP_CONTENT_UPDATE_KEY] = updateTooltipContent;
    },
    updated(el, binding) {
        const value = binding.value ?? '';
        const content = getTooltipContent(value);
        const allowHtml = getAllowHtml(value);
        el[TOOLTIP_CONTENT_UPDATE_KEY]?.(content, allowHtml);
    },
    beforeUnmount(el) {
        el[TOOLTIP_CLEANUP_KEY]?.();
        delete el[TOOLTIP_CLEANUP_KEY];
        delete el[TOOLTIP_CONTENT_UPDATE_KEY];
    },
};
